import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { RouteComponentProps } from '@reach/router';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Autocomplete,
  Box,
  FormControlLabel,
  IconButton,
  OutlinedInput,
  Snackbar,
  Stack,
  Switch,
  TextField as MuiTextField,
  Tooltip
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { Alert, Button, Modal, TextField, TextView } from '@atoms';
import { callGetLookups, callUpsertLookup, callDeleteLookup } from '@api';
import type { LookupItem } from '@api';

type RowDraft = {
  value: string;
  label: string;
  groupName: string;
  sortOrder: string;
  isActive: boolean;
};

const toDraft = (item: LookupItem): RowDraft => ({
  value: item.value ?? '',
  label: item.label ?? '',
  groupName: item.groupName ?? '',
  sortOrder: String(item.sortOrder ?? 0),
  isActive: item.isActive ?? true
});

const CATEGORY_HELP: Record<string, string> = {
  config:
    'Global scalar settings. sr_token_conversion_rate is the SR-token divisor used by AI, Twilio and WhatsApp billing; sr_token_conversion_rate:reapit overrides it for Reapit/Negotiator billing. Changes apply to the next billed usage.'
};

// Categories that moved to a dedicated table and are no longer read. Their rows
// are left in Lookups untouched (for rollback) but hidden here so nobody edits a
// list that has no effect. `ai_model` → AiModels (migration 137), managed under
// the AI Models page (/ai-models).
const RETIRED_CATEGORIES = new Set(['ai_model']);

// Friendly section titles. Unknown categories are prettified automatically
// (snake/kebab → Title Case), so new categories still read nicely.
const CATEGORY_TITLES: Record<string, string> = {
  config: 'Config',
  integration: 'Integrations'
};

const titleize = (s: string): string =>
  s.replace(/[_-]+/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

const categoryTitle = (category: string): string => CATEGORY_TITLES[category] ?? titleize(category);

const SuperAdminLookups: React.FC<RouteComponentProps> = () => {
  const [items, setItems] = useState<LookupItem[]>([]);
  const [drafts, setDrafts] = useState<Record<string, RowDraft>>({});
  const [busyId, setBusyId] = useState<string | null>(null);
  const [snack, setSnack] = useState<{ open: boolean; severity: 'success' | 'error'; message: string }>({
    open: false,
    severity: 'success',
    message: ''
  });

  // Add dialog
  const [dialogOpen, setDialogOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newCategory, setNewCategory] = useState('');
  const [newKey, setNewKey] = useState('');
  const [newValue, setNewValue] = useState('');
  const [newLabel, setNewLabel] = useState('');
  const [newGroup, setNewGroup] = useState('');
  const [newSortOrder, setNewSortOrder] = useState('0');

  const closeSnack = useCallback(() => setSnack((s) => ({ ...s, open: false })), []);

  const lookupsQuery = useQuery({
    queryKey: ['super-admin/lookups'],
    queryFn: () => callGetLookups(),
    // Config-style data that only changes when a superadmin edits it on
    // this very page (which already calls refetch on save).
    staleTime: 30 * 60 * 1000
  });
  const loading = lookupsQuery.isFetching;

  // Refetch is the new equivalent of loadLookups — it goes through React Query
  // so a hit while still fresh is served from cache instantly.
  const loadLookups = useCallback(async () => {
    await lookupsQuery.refetch();
  }, [lookupsQuery]);

  // Mirror the server payload into the local items+drafts state. The page
  // mutates `items` locally after save/delete to avoid an immediate refetch
  // (lines below); useQuery only owns the load + invalidation path.
  useEffect(() => {
    if (lookupsQuery.data?.code !== 200) return;
    const rows = lookupsQuery.data.data?.data ?? [];
    setItems(rows);
    const nextDrafts: Record<string, RowDraft> = {};
    rows.forEach((row) => {
      nextDrafts[row.id] = toDraft(row);
    });
    setDrafts(nextDrafts);
  }, [lookupsQuery.data]);

  useEffect(() => {
    if (lookupsQuery.isError) {
      console.error('Error loading lookups:', lookupsQuery.error);
      setSnack({ open: true, severity: 'error', message: 'Failed to load lookups.' });
    }
  }, [lookupsQuery.isError, lookupsQuery.error]);

  const grouped = useMemo(() => {
    const byCategory: Record<string, LookupItem[]> = {};
    items.forEach((item) => {
      if (RETIRED_CATEGORIES.has(item.category)) return;
      (byCategory[item.category] ??= []).push(item);
    });
    return Object.keys(byCategory)
      .sort()
      .map((category) => {
        const rows = byCategory[category];
        // Distinct existing groups in this category — used to suggest values
        // (and keep them consistent) without forcing free-text retyping.
        const groupOptions = Array.from(
          new Set(rows.map((r) => r.groupName).filter((g): g is string => Boolean(g)))
        ).sort();
        return { category, rows, groupOptions };
      });
  }, [items]);

  const onDraftChange = useCallback((id: string, field: keyof RowDraft, value: string | boolean) => {
    setDrafts((prev) => ({ ...prev, [id]: { ...prev[id], [field]: value } }));
  }, []);

  const isRowDirty = useCallback(
    (item: LookupItem): boolean => {
      const d = drafts[item.id];
      if (!d) return false;
      const orig = toDraft(item);
      return (
        d.value !== orig.value ||
        d.label !== orig.label ||
        d.groupName !== orig.groupName ||
        d.sortOrder !== orig.sortOrder ||
        d.isActive !== orig.isActive
      );
    },
    [drafts]
  );

  const saveRow = useCallback(
    async (item: LookupItem) => {
      const d = drafts[item.id];
      if (!d) return;
      const sortOrderNum = Number(d.sortOrder);
      if (!Number.isFinite(sortOrderNum)) {
        setSnack({ open: true, severity: 'error', message: 'Sort order must be a number.' });
        return;
      }
      setBusyId(item.id);
      try {
        const res = await callUpsertLookup({
          id: item.id,
          category: item.category,
          key: item.key,
          value: d.value,
          label: d.label || null,
          groupName: d.groupName || null,
          sortOrder: sortOrderNum,
          isActive: d.isActive
        });
        if (res.code === 200 || res.code === 201) {
          setItems((prev) =>
            prev.map((it) =>
              it.id === item.id
                ? { ...it, value: d.value, label: d.label, groupName: d.groupName, sortOrder: sortOrderNum, isActive: d.isActive }
                : it
            )
          );
          setSnack({ open: true, severity: 'success', message: `Saved ${item.category} / ${item.key}.` });
        } else {
          setSnack({ open: true, severity: 'error', message: 'Failed to save lookup.' });
        }
      } catch (error) {
        console.error('Error saving lookup:', error);
        setSnack({ open: true, severity: 'error', message: 'Failed to save lookup.' });
      } finally {
        setBusyId(null);
      }
    },
    [drafts]
  );

  const deleteRow = useCallback(async (item: LookupItem) => {
    setBusyId(item.id);
    try {
      const res = await callDeleteLookup(item.id);
      if (res.code === 200) {
        setItems((prev) => prev.filter((it) => it.id !== item.id));
        setSnack({ open: true, severity: 'success', message: `Deleted ${item.category} / ${item.key}.` });
      } else {
        setSnack({ open: true, severity: 'error', message: 'Failed to delete lookup.' });
      }
    } catch (error) {
      console.error('Error deleting lookup:', error);
      setSnack({ open: true, severity: 'error', message: 'Failed to delete lookup.' });
    } finally {
      setBusyId(null);
    }
  }, []);

  const openAddDialog = useCallback((category?: string) => {
    setNewCategory(category ?? '');
    setNewKey('');
    setNewValue('');
    setNewLabel('');
    setNewGroup('');
    setNewSortOrder('0');
    setDialogOpen(true);
  }, []);

  const handleCreate = useCallback(async () => {
    if (!newCategory.trim() || !newKey.trim()) {
      setSnack({ open: true, severity: 'error', message: 'Category and key are required.' });
      return;
    }
    const sortOrderNum = Number(newSortOrder);
    if (!Number.isFinite(sortOrderNum)) {
      setSnack({ open: true, severity: 'error', message: 'Sort order must be a number.' });
      return;
    }
    setCreating(true);
    try {
      const res = await callUpsertLookup({
        category: newCategory.trim(),
        key: newKey.trim(),
        value: newValue,
        label: newLabel || null,
        groupName: newGroup || null,
        sortOrder: sortOrderNum,
        isActive: true
      });
      if (res.code === 200 || res.code === 201) {
        setSnack({ open: true, severity: 'success', message: 'Lookup saved.' });
        setDialogOpen(false);
        await loadLookups();
      } else {
        setSnack({ open: true, severity: 'error', message: 'Failed to create lookup.' });
      }
    } catch (error) {
      console.error('Error creating lookup:', error);
      setSnack({ open: true, severity: 'error', message: 'Failed to create lookup.' });
    } finally {
      setCreating(false);
    }
  }, [newCategory, newKey, newValue, newLabel, newGroup, newSortOrder, loadLookups]);

  return (
    <Box sx={{ px: 5, py: 4 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
        <TextView $fontSize={12} $fontWeight={400} $fontColor="#445" style={{ opacity: 0.9 }}>
          Manage product reference data and global settings in one place. Each entry is keyed by category + key.
        </TextView>
        <Stack direction="row" spacing={1.25}>
          <Button onClick={() => openAddDialog()} startIcon={<AddIcon />}>
            Add lookup
          </Button>
          <Button primary={false} variant="text" onClick={loadLookups} disabled={loading}>
            Reload
          </Button>
        </Stack>
      </Stack>

      <Stack spacing={1.5}>
        {grouped.map((group, idx) => (
          <Accordion
            key={group.category}
            defaultExpanded={idx === 0}
            disableGutters
            sx={{ backgroundColor: '#f3f6f4', borderRadius: 1.5, '&:before': { display: 'none' } }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 2, py: 1 }}>
              <Stack spacing={0.25}>
                <TextView $fontSize={16} $fontWeight={800}>
                  {categoryTitle(group.category)}
                </TextView>
                {CATEGORY_HELP[group.category] ? (
                  <TextView $fontSize={11} $fontWeight={400} $fontColor="#445" style={{ opacity: 0.9 }}>
                    {CATEGORY_HELP[group.category]}
                  </TextView>
                ) : null}
              </Stack>
            </AccordionSummary>

            <AccordionDetails sx={{ px: 2, pb: 2 }}>
              <Stack spacing={0.75}>
                <Stack
                  direction="row"
                  spacing={1.25}
                  sx={{ px: 1.25, pt: 0.5, pb: 0.25, display: { xs: 'none', md: 'flex' } }}
                >
                  <Box sx={{ width: 220 }}>
                    <TextView $fontSize={11} $fontWeight={700} $fontColor="#64748b">KEY</TextView>
                  </Box>
                  <Box sx={{ flex: 2, minWidth: 140 }}>
                    <TextView $fontSize={11} $fontWeight={700} $fontColor="#64748b">VALUE</TextView>
                  </Box>
                  <Box sx={{ flex: 1.5, minWidth: 120 }}>
                    <TextView $fontSize={11} $fontWeight={700} $fontColor="#64748b">LABEL</TextView>
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 140 }}>
                    <TextView $fontSize={11} $fontWeight={700} $fontColor="#64748b">GROUP</TextView>
                  </Box>
                  <Box sx={{ width: 90 }}>
                    <TextView $fontSize={11} $fontWeight={700} $fontColor="#64748b">ORDER</TextView>
                  </Box>
                  <Box sx={{ width: 100 }}>
                    <TextView $fontSize={11} $fontWeight={700} $fontColor="#64748b">ACTIVE</TextView>
                  </Box>
                  <Box sx={{ width: 140 }}>
                    <TextView $fontSize={11} $fontWeight={700} $fontColor="#64748b">ACTIONS</TextView>
                  </Box>
                </Stack>
                {group.rows.map((item) => {
                  const d = drafts[item.id] ?? toDraft(item);
                  const dirty = isRowDirty(item);
                  const busy = busyId === item.id;
                  return (
                    <Stack
                      key={item.id}
                      direction={{ xs: 'column', md: 'row' }}
                      spacing={1.25}
                      alignItems={{ xs: 'stretch', md: 'center' }}
                      sx={{ px: 1.25, py: 0.75, borderRadius: 1, backgroundColor: 'rgba(255,255,255,0.65)' }}
                    >
                      <Box sx={{ width: { xs: '100%', md: 220 }, minWidth: 0 }}>
                        <Box
                          sx={{
                            display: 'inline-flex',
                            maxWidth: '100%',
                            px: 0.75,
                            py: 0.1,
                            borderRadius: 0.75,
                            backgroundColor: '#eef2ff',
                            border: '1px solid #e0e7ff'
                          }}
                        >
                          <TextView
                            $fontSize={10}
                            $fontWeight={700}
                            $fontColor="#4f46e5"
                            style={{ fontFamily: 'monospace' }}
                            sx={{
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis'
                            }}
                          >
                            {item.key}
                          </TextView>
                        </Box>
                      </Box>

                      <Box sx={{ flex: 2, minWidth: 140 }}>
                        <OutlinedInput
                          value={d.value}
                          size="small"
                          fullWidth
                          placeholder="value"
                          onChange={(e) => onDraftChange(item.id, 'value', e.target.value)}
                          inputProps={{ 'aria-label': 'value', style: { fontSize: 13 } }}
                        />
                      </Box>
                      <Box sx={{ flex: 1.5, minWidth: 120 }}>
                        <OutlinedInput
                          value={d.label}
                          size="small"
                          fullWidth
                          placeholder="label"
                          onChange={(e) => onDraftChange(item.id, 'label', e.target.value)}
                          inputProps={{ 'aria-label': 'label', style: { fontSize: 13 } }}
                        />
                      </Box>
                      <Box sx={{ flex: 1, minWidth: 140 }}>
                        <Autocomplete
                          freeSolo
                          fullWidth
                          size="small"
                          options={group.groupOptions}
                          inputValue={d.groupName}
                          onInputChange={(_, newInput) => onDraftChange(item.id, 'groupName', newInput)}
                          renderInput={(params) => (
                            <MuiTextField
                              {...params}
                              size="small"
                              placeholder="group"
                              inputProps={{ ...params.inputProps, 'aria-label': 'group', style: { fontSize: 13 } }}
                            />
                          )}
                        />
                      </Box>
                      <Box sx={{ width: 90 }}>
                        <OutlinedInput
                          value={d.sortOrder}
                          size="small"
                          type="number"
                          fullWidth
                          onChange={(e) => onDraftChange(item.id, 'sortOrder', e.target.value)}
                          inputProps={{ 'aria-label': 'sort order', style: { fontSize: 13 } }}
                        />
                      </Box>
                      <Box sx={{ width: 100 }}>
                        <FormControlLabel
                          control={
                            <Switch
                              size="small"
                              checked={d.isActive}
                              onChange={(e) => onDraftChange(item.id, 'isActive', e.target.checked)}
                            />
                          }
                          label={<TextView $fontSize={11}>Active</TextView>}
                        />
                      </Box>
                      <Stack direction="row" spacing={0.5} alignItems="center" sx={{ width: 140 }}>
                        <Button onClick={() => saveRow(item)} loading={busy} disabled={!dirty || busy}>
                          Save
                        </Button>
                        <Tooltip title="Delete">
                          <span>
                            <IconButton size="small" onClick={() => deleteRow(item)} disabled={busy}>
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Stack>
                    </Stack>
                  );
                })}

                <Box sx={{ pt: 0.5 }}>
                  <Button primary={false} variant="outlined" onClick={() => openAddDialog(group.category)} startIcon={<AddIcon />}>
                    Add to {categoryTitle(group.category)}
                  </Button>
                </Box>
              </Stack>
            </AccordionDetails>
          </Accordion>
        ))}
        {grouped.length === 0 && !loading ? (
          <TextView $fontSize={13} $fontColor="#445">
            No lookups yet. Use “Add lookup” to create one.
          </TextView>
        ) : null}
      </Stack>

      <Modal open={dialogOpen} onClose={() => setDialogOpen(false)}>
        <Box sx={{ width: 480, maxWidth: '95vw' }}>
          <TextView $fontSize={18} $fontWeight={800} mb={2}>
            Add lookup
          </TextView>
          <Stack spacing={1.5}>
            <TextField
              value={newCategory}
              onChange={(e) => setNewCategory(e.target.value)}
              label="Category"
              labelSize={12}
              labelWeight={700}
              size="small"
              fullWidth
              name="lookup.category"
            />
            <TextField
              value={newKey}
              onChange={(e) => setNewKey(e.target.value)}
              label="Key"
              labelSize={12}
              labelWeight={700}
              size="small"
              fullWidth
              name="lookup.key"
            />
            <TextField
              value={newValue}
              onChange={(e) => setNewValue(e.target.value)}
              label="Value"
              labelSize={12}
              labelWeight={700}
              size="small"
              fullWidth
              name="lookup.value"
            />
            <Stack direction="row" spacing={1.25}>
              <TextField
                value={newLabel}
                onChange={(e) => setNewLabel(e.target.value)}
                label="Label (optional)"
                labelSize={12}
                labelWeight={700}
                size="small"
                fullWidth
                name="lookup.label"
              />
              <TextField
                value={newGroup}
                onChange={(e) => setNewGroup(e.target.value)}
                label="Group (optional)"
                labelSize={12}
                labelWeight={700}
                size="small"
                fullWidth
                name="lookup.group"
              />
            </Stack>
            <TextField
              value={newSortOrder}
              onChange={(e) => setNewSortOrder(e.target.value)}
              label="Sort order"
              labelSize={12}
              labelWeight={700}
              size="small"
              type="number"
              fullWidth
              name="lookup.sortOrder"
            />
            <Stack direction="row" spacing={1.25} justifyContent="flex-end" sx={{ pt: 1 }}>
              <Button primary={false} variant="text" onClick={() => setDialogOpen(false)} disabled={creating}>
                Cancel
              </Button>
              <Button onClick={handleCreate} loading={creating} disabled={creating}>
                Save
              </Button>
            </Stack>
          </Stack>
        </Box>
      </Modal>

      <Snackbar open={snack.open} autoHideDuration={4000} onClose={closeSnack} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <Box>
          <Alert message={snack.message} severity={snack.severity} variant="filled" onClose={closeSnack} />
        </Box>
      </Snackbar>
    </Box>
  );
};

export default SuperAdminLookups;
